import type { SaleExchangeStatusInfo, SaleOrder } from '@/types/sell';

/**
 * Состояние биржевого оффера для UI (бейдж + набор действий). `null` — оффер не биржевой
 * (`toExchange=false`), для него действует обычный жизненный цикл (пауза/возобновление).
 * - ON_EXCHANGE — живая заявка на бирже: правка/удаление заморожены, доступно «Снять с биржи».
 * - OFF_EXCHANGE — биржевой, но не опубликован (создан вне окна, снят или сессия закрыта): можно
 *   публиковать (в окне), править, удалять, сделать внебиржевым.
 * - FAILED — биржа отклонила: как OFF_EXCHANGE + причина и «Опубликовать снова».
 * - MATCHED — заявка исполнена, оффер в сделке (только чтение, ищем в «Сделках»).
 */
export type ExchangeUiState = 'ON_EXCHANGE' | 'OFF_EXCHANGE' | 'FAILED' | 'MATCHED';

/** Выводит биржевое UI-состояние из оффера и его биржевого статуса; `null` для внебиржевого оффера. */
export function exchangeUiState(offer: SaleOrder, info?: SaleExchangeStatusInfo): ExchangeUiState | null {
  if (!offer.toExchange) {
    return null;
  }
  // Сделка авторитетна и по платформенному статусу оффера, и по биржевой строке (MATCHED).
  if (offer.status === 'UNDER_BARGAIN' || info?.status === 'MATCHED') {
    return 'MATCHED';
  }
  switch (info?.status) {
    case 'ON_EXCHANGE':
      return 'ON_EXCHANGE';
    case 'FAILED':
      return 'FAILED';
    default:
      return 'OFF_EXCHANGE';
  }
}

/** Заморожен ли оффер живой биржевой заявкой (правка/удаление запрещены до снятия с биржи). */
export function isFrozenByExchange(state: ExchangeUiState | null): boolean {
  return state === 'ON_EXCHANGE';
}

/** Можно ли сейчас публиковать биржевой оффер (не на бирже/отклонён — публикуемые состояния). */
export function canPublishToExchange(state: ExchangeUiState | null): boolean {
  return state === 'OFF_EXCHANGE' || state === 'FAILED';
}
